import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api-client";
import { passwordHintsFor, validatePassword } from "@/lib/passwordPolicy";
import { usePasswordMinLength } from "@/lib/queries/useSecurityPolicy";
import { fireToast } from "@/lib/toast";
import { apiErrorMessage } from "@/lib/apiErrors";

/**
 * 비밀번호 변경 폼 (공용) — 사용자 설정 화면(AccountSettingsPage)과
 * 비밀번호 만료 하드게이트(PasswordExpiredGate)에서 재사용.
 *
 * - 클라이언트 정책 사전검증(validatePassword) 후 POST /api/account/password.
 * - 최종 정책(길이·이력)은 서버가 권위(전역 보안정책 companies '0'). 400 사유를 그대로 노출.
 * - 성공 시 필드 초기화 + 토스트 + onSuccess 콜백(예: /me 무효화로 만료 플래그 해제).
 */
export interface ChangePasswordFormProps {
  /** 변경 성공 시 호출(예: /me 재조회로 passwordExpired 해제). */
  onSuccess?: () => void;
  /** 제출 버튼 라벨(기본 "변경"). 하드게이트는 "변경하고 계속"으로 override. */
  submitLabel?: string;
  /**
   * 제출 버튼 왼쪽 보조 동작(예: 하드게이트의 [로그아웃]). 있으면 하단 줄이
   * `[보조] · 빈 공간 · [제출]`(규칙 ui-button-placement §1), 없으면 제출 버튼만 오른쪽.
   */
  secondaryAction?: ReactNode;
}

export function ChangePasswordForm({
  onSuccess,
  submitLabel,
  secondaryAction,
}: ChangePasswordFormProps) {
  const { t } = useTranslation();
  // 전역 보안정책의 최소 길이(std 8·strong 12·max 14) — 사전검증·힌트 모두 서버값 기준.
  const minLength = usePasswordMinLength();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  /** 비밀번호 변경 제출 — 클라이언트 정책 검증 후 POST /api/account/password. */
  async function onChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);

    const { valid, errors } = validatePassword(newPassword, minLength);
    if (!valid) {
      setPwError(errors.join(", "));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError(t("account.pw.mismatch", "새 비밀번호와 확인 비밀번호가 일치하지 않습니다."));
      return;
    }

    setPwSaving(true);
    try {
      await apiFetch<void>("/api/account/password", {
        method: "POST",
        body: { currentPassword, newPassword },
      });
      fireToast(t("account.pw.changed", "비밀번호가 변경되었습니다."));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess?.();
    } catch (err) {
      const e = err as { status?: number };
      if (e.status === 400) {
        // 서버가 400 사유(현재 비번 불일치 / 정책 위반)를 detail 로 보내므로 그대로 노출함.
        // 새 비번 정책은 이미 위에서 클라이언트 검증하므로, 이 400 은 사실상 현재 비밀번호 불일치임.
        setPwError(
          apiErrorMessage(err) ||
            t("account.pw.wrongCurrent", "현재 비밀번호가 올바르지 않습니다."),
        );
      } else {
        setPwError(t("account.pw.error", "비밀번호 변경에 실패했습니다. 다시 시도하세요."));
      }
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void onChangePassword(e);
      }}
    >
      <Stack gap="sm">
        <TextInput
          label={t("account.pw.current", "현재 비밀번호")}
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.currentTarget.value)}
          required
        />
        <TextInput
          label={t("account.pw.new", "새 비밀번호")}
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.currentTarget.value)}
          required
        />
        {/* 정책 힌트 체크리스트 — 입력 시작 후 표시 */}
        {newPassword.length > 0 && (
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 12,
              lineHeight: 1.7,
              color: "var(--text-3)",
            }}
          >
            {passwordHintsFor(minLength).map((hint) => (
              <li
                key={hint.message}
                style={{
                  color: hint.test(newPassword) ? "var(--ok, #16a34a)" : "var(--text-3)",
                }}
              >
                {hint.message}
              </li>
            ))}
          </ul>
        )}
        <TextInput
          label={t("account.pw.confirm", "새 비밀번호 확인")}
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.currentTarget.value)}
          required
        />
        {pwError && (
          <Text size="sm" c="red" role="alert">
            {pwError}
          </Text>
        )}
        <Group justify="flex-end" gap={10}>
          {secondaryAction}
          {secondaryAction != null && <div style={{ flex: 1 }} />}
          <Button type="submit" loading={pwSaving}>
            {submitLabel ?? t("account.pw.change", "변경")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
